import katex from "katex";
import "katex/dist/katex.min.css";

// ============ helpers ============
const escapeHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// ---------- MATRIX: split/normalize only inside explicit *matrix envs ----------
const splitRowsInEnv = (s) =>
  s
    .split(/(?:\s*;+\s*|\s*\\\\+\s*|\s*\n+\s*|\s*\\(?![a-zA-Z])\s*)/g)
    .map((r) => r.trim())
    .filter(Boolean);

// cells separated by commas/spaces or a STANDALONE "\" (not starting a command)
const CELL_SPLIT_RE = /(?:[, \t]+|\\(?![a-zA-Z]))+/g;

const rowToLatex = (row) => {
  if (row.includes("&")) return row; // already cells joined with &
  const cells = row
    .trim()
    .split(CELL_SPLIT_RE)
    .map((c) => c.trim())
    .filter(Boolean);
  return cells.join(" & ");
};

// fix missing "\" before begin/end for *matrix envs and collapse stray slashes
const fixLooseMatrixBeginEnd = (s) =>
  s
    .replace(/(^|[^\\])\bbegin\{([pPbBvV]?matrix)\}/g, (m, pre, env) => `${pre}\\begin{${env}}`)
    .replace(/(^|[^\\])\bend\{([pPbBvV]?matrix)\}/g, (m, pre, env) => `${pre}\\end{${env}}`)
    .replace(/(\\\s*)+(?=\\end\{[pPbBvV]?matrix\})/g, "\\");

const normalizeMatrixEnvs = (content) =>
  content.replace(/\\begin\{([pPbBvV]?matrix)\}([\s\S]*?)\\end\{\1\}/g, (m, env, inner) => {
    const rows = splitRowsInEnv(inner);
    if (!rows.length) return `\\begin{${env}}\\end{${env}}`;
    const latexRows = rows.map(rowToLatex).join(" \\\\ ");
    return `\\begin{${env}} ${latexRows} \\end{${env}}`;
  });

// repair ONLY *inside* explicit matrix envs
const repairMatricesInMath = (math) => {
  let s = String(math).trim();
  s = fixLooseMatrixBeginEnd(s);
  s = normalizeMatrixEnvs(s);
  return s;
};

// ---------- DOLLAR-MATH: strip inner \( \) \[ \] only inside $...$/$$...$$ ----------
function replaceSingleBackslashToken(s, token, replacement) {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s.slice(i, i + token.length) === token) {
      if (i > 0 && s[i - 1] === "\\") {
        out += token; // escaped backslash, keep literal
      } else {
        out += replacement;
      }
      i += token.length - 1;
    } else {
      out += s[i];
    }
  }
  return out;
}
function stripInnerTextDelims(inner) {
  let s = String(inner);
  s = replaceSingleBackslashToken(s, "\\(", "(");
  s = replaceSingleBackslashToken(s, "\\)", ")");
  s = replaceSingleBackslashToken(s, "\\[", "[");
  s = replaceSingleBackslashToken(s, "\\]", "]");
  return s;
}
function normalizeMixedDelimsInDollars(text) {
  const str = String(text);
  let out = "";
  for (let i = 0; i < str.length; ) {
    const ch = str[i];
    // keep escaped chars like "\$" literal
    if (ch === "\\") {
      out += ch;
      if (i + 1 < str.length) {
        out += str[i + 1];
        i += 2;
      } else {
        i += 1;
      }
      continue;
    }
    if (ch === "$") {
      // $$...$$
      if (i + 1 < str.length && str[i + 1] === "$") {
        const start = i + 2;
        const end = str.indexOf("$$", start);
        if (end !== -1) {
          const inner = str.slice(start, end);
          out += "$$" + stripInnerTextDelims(inner) + "$$";
          i = end + 2;
          continue;
        } else {
          out += "$$"; i += 2; continue;
        }
      }
      // $...$
      let j = i + 1;
      for (; j < str.length; j++) {
        if (str[j] === "\\") { j++; continue; } // skip escaped char
        if (str[j] === "$") break;
      }
      if (j < str.length && str[j] === "$") {
        const inner = str.slice(i + 1, j);
        out += "$" + stripInnerTextDelims(inner) + "$";
        i = j + 1; continue;
      } else {
        out += "$"; i += 1; continue;
      }
    }
    out += ch; i += 1;
  }
  return out;
}

// ---------- MATH-ONLY ASCII → LaTeX normalizer (logic, arrows, greek, functions, sets) ----------
function protectTextBlocks(s) {
  let out = "", i = 0;
  const blocks = [];
  while (i < s.length) {
    if (s.slice(i, 6) === "\\text{") {
      let j = i + 6, depth = 1;
      while (j < s.length && depth > 0) {
        if (s[j] === "{") depth++;
        else if (s[j] === "}") depth--;
        j++;
      }
      const block = s.slice(i, j);
      const token = `__TEXTPROT_${blocks.length}__`;
      blocks.push(block);
      out += token;
      i = j;
    } else {
      out += s[i++];
    }
  }
  return { s: out, blocks };
}
function unprotectTextBlocks(s, blocks) {
  return s.replace(/__TEXTPROT_(\d+)__/g, (_, idx) => blocks[Number(idx)]);
}
// replace standalone word tokens not preceded/followed by letters or a backslash
function replaceWordToken(str, word, latex) {
  const re = new RegExp(`(^|[^\\\\A-Za-z])(${word})(?=$|[^A-Za-z])`, "gi");
  return str.replace(re, (_, pre) => `${pre}${latex}`);
}
// replace ASCII sequences like "->", "<->" when not escaped
function replaceLiteralToken(str, literal, latex) {
  const esc = literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(^|[^\\\\])${esc}`, "g");
  return str.replace(re, (_, pre) => `${pre}${latex}`);
}

// MAIN: normalize common ASCII math/logic ONLY inside math snippets
function normalizeAsciiMathInMath(s) {
  const prot = protectTextBlocks(String(s));
  let t = prot.s;

  // --- salvage after JSON escapes: \r, \n eaten ---
  t = replaceWordToken(t, "ightarrow", "\\rightarrow "); // from "\r" → CR + "ightarrow"
  t = replaceWordToken(t, "eg", "\\neg ");               // from "\n" → LF + "eg"

  // --- arrows / equivalence (no leading backslash present) ---
  t = replaceWordToken(t, "rightarrow", "\\rightarrow ");
  t = replaceWordToken(t, "leftarrow", "\\leftarrow ");
  t = replaceWordToken(t, "leftrightarrow", "\\leftrightarrow ");
  t = replaceWordToken(t, "Rightarrow", "\\Rightarrow ");
  t = replaceWordToken(t, "Leftarrow", "\\Leftarrow ");
  t = replaceWordToken(t, "Leftrightarrow", "\\Leftrightarrow ");

  // --- ASCII operators you already had ---
  t = replaceLiteralToken(t, "<=>", "\\Leftrightarrow ");
  t = replaceLiteralToken(t, "<->", "\\leftrightarrow ");
  t = replaceLiteralToken(t, "=>",  "\\Rightarrow ");
  t = replaceLiteralToken(t, "->",  "\\rightarrow ");

  // --- logic words (no leading backslash) ---
  t = replaceWordToken(t, "iff", "\\iff ");
  t = replaceWordToken(t, "implies", "\\Rightarrow ");
  t = replaceWordToken(t, "sim", "\\sim ");
  t = replaceWordToken(t, "neg", "\\neg ");
  t = replaceWordToken(t, "not", "\\neg ");
  t = replaceWordToken(t, "land", "\\land ");
  t = replaceWordToken(t, "and", "\\land ");
  t = replaceWordToken(t, "lor", "\\lor ");
  t = replaceWordToken(t, "or", "\\lor ");
  t = replaceWordToken(t, "xor", "\\oplus ");

  // ... keep the rest of your mappings (functions, sets, greek, unicode → LaTeX) ...

  return unprotectTextBlocks(t, prot.blocks);
}


// ============ renderers ============
const renderMath = (mathContent, displayMode) => {
  // 1) normalize ASCII/word operators INSIDE math
  const normalized = normalizeAsciiMathInMath(mathContent);
  // 2) repair matrices ONLY in explicit matrix envs
  const repaired = repairMatricesInMath(normalized);
  try {
    return katex.renderToString(repaired, {
      displayMode,
      throwOnError: false,
      strict: "warn",
    });
  } catch (e) {
    console.warn("KaTeX rendering error:", e, "\ninput:", mathContent, "\nnormalized:", normalized, "\nrepaired:", repaired);
    return `<span style="color:red;">[LaTeX Error: ${escapeHtml(
      e.message || "render"
    )}]</span>${escapeHtml(mathContent)}`;
  }
};

export const renderLatex = (text) => {
  if (!text) return text;
  try {
    let processedText = String(text);

    // SMART unescape: \\begin, \\end, \\[, \\], \\(, \\)
    processedText = processedText.replace(/\\\\(?=(?:begin|end)\b|[\[\]\(\)])/g, "\\");

    // PRE-NORMALIZE: inside $...$/$$...$$, strip inner \( \) \[ \]
    processedText = normalizeMixedDelimsInDollars(processedText);

    // DISPLAY MATH — $$...$$
    processedText = processedText.replace(/\$\$([\s\S]+?)\$\$/g, (_, m) => renderMath(m, true));
    // INLINE MATH — $...$
    processedText = processedText.replace(/\$([\s\S]+?)\$/g, (_, m) => renderMath(m, false));
    // DISPLAY with \[...\] (outside dollar math only)
    processedText = processedText.replace(/\\\[([\s\S]+?)\\\]/g, (_, m) => renderMath(m, true));
    // INLINE with \(...\) (outside dollar math only)
    processedText = processedText.replace(/\\\(([\s\S]+?)\\\)/g, (_, m) => renderMath(m, false));

    return processedText;
  } catch (error) {
    console.warn("Overall LaTeX processing error:", error);
    return `<span style="color:red;">[Overall LaTeX Processing Error: ${escapeHtml(
      error.message || "unknown"
    )}]</span>${escapeHtml(String(text))}`;
  }
};
